import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PackagePlus, Plus, Sparkles } from 'lucide-react';
import { usePiercingWorkflow, type PiercingAct, type WorkflowSupplement } from '../contexts/PiercingWorkflowContext';
import PiercingWorkflowStepper from './PiercingWorkflowStepper';
import { apiClient } from '../utils/api';

function actPiercingTypeId(act: PiercingAct): string | undefined {
  return act.demandDetails?.piercingTypeModelId ?? act.piercingRecord?.piercingTypeModel?.id;
}

type AppointmentProduct = {
  id: string;
  productCode: string;
  name: string;
  description?: string | null;
  price: number;
  piercingTypeId?: string | null;
  piercingTypeName?: string | null;
};

function productDisplayName(product: AppointmentProduct): string {
  return product.description?.trim() || product.name;
}

type PricingPiercingType = {
  id?: string;
  piercingTypeModelId?: string;
  piercingTypeId?: string;
  name?: string;
  price?: number;
  basePrice?: number;
  customPrice?: number;
};

function pricingEntryId(pt: PricingPiercingType): string | undefined {
  const raw = pt.id ?? pt.piercingTypeModelId ?? pt.piercingTypeId;
  return raw != null ? String(raw) : undefined;
}

function pricingEntryPrice(pt: PricingPiercingType | undefined): number | null {
  if (!pt) return null;
  for (const key of ['price', 'basePrice', 'customPrice'] as const) {
    const v = pt[key];
    if (v != null && !Number.isNaN(Number(v))) return Math.max(0, Number(v));
  }
  return null;
}

function formatMoneyEur(amount: number): string {
  if (!Number.isFinite(amount)) return '0';
  const rounded = Math.round(amount * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

function actModelPrice(act: PiercingAct): { amount: number; name?: string } | null {
  const model = act.piercingRecord?.piercingTypeModel as { name?: string; basePrice?: number } | undefined;
  if (model?.basePrice != null && !Number.isNaN(Number(model.basePrice))) {
    return { amount: Math.max(0, Number(model.basePrice)), name: model.name };
  }
  const servicePrice = (act.service as { basePrice?: number } | undefined)?.basePrice;
  if (servicePrice != null && !Number.isNaN(Number(servicePrice))) {
    return { amount: Math.max(0, Number(servicePrice)) };
  }
  return null;
}

/** Une ligne par acte : prix issu de la réservation, du type choisi, ou estimé si acte ajouté en cours de route. */
function buildPiercingSaleLines(
  acts: PiercingAct[],
  pricing: Record<string, unknown> | undefined,
): { lines: { key: string; label: string; amount: number }[]; subtotal: number } {
  const p = pricing ?? {};
  const pts = Array.isArray(p.piercingTypes)
    ? (p.piercingTypes as PricingPiercingType[])
    : [];
  const nBooked = pts.length;
  const pricedFromPts = pts
    .map((x) => pricingEntryPrice(x))
    .filter((v): v is number => v != null);
  const sumBooked = pricedFromPts.reduce((s, v) => s + v, 0);
  const baseFallback = Number(p.basePrice) || 0;
  const totalFallback = Number(p.subtotal ?? p.total ?? p.totalPrice) || 0;
  const avgFromBase =
    baseFallback > 0 && acts.length > 0 ? baseFallback / acts.length : 0;
  const avgFromTotal =
    totalFallback > 0 && acts.length > 0 ? totalFallback / acts.length : 0;
  const avgFromBooked =
    nBooked > 0 && sumBooked > 0 ? sumBooked / nBooked : 0;

  const lines: { key: string; label: string; amount: number }[] = [];
  for (let i = 0; i < acts.length; i++) {
    const act = acts[i];
    const tid = actPiercingTypeId(act);
    const ptByIndex = i < nBooked ? pts[i] : undefined;
    const ptByType = tid ? pts.find((x) => pricingEntryId(x) === tid) : undefined;

    let amount: number | null = pricingEntryPrice(ptByType ?? undefined);
    let nameBit =
      ptByType?.name ?? ptByIndex?.name ?? act.demandDetails?.type ?? undefined;

    if (amount == null) {
      amount = pricingEntryPrice(ptByIndex);
    }

    if (amount == null) {
      const fromModel = actModelPrice(act);
      if (fromModel) {
        amount = fromModel.amount;
        nameBit = nameBit ?? fromModel.name;
      }
    }

    if (amount == null) {
      if (avgFromBooked > 0) {
        amount = avgFromBooked;
      } else if (avgFromBase > 0) {
        amount = avgFromBase;
      } else if (avgFromTotal > 0) {
        amount = avgFromTotal;
      } else {
        amount = 0;
      }
    }

    if (!nameBit) {
      const modelName = (act.piercingRecord?.piercingTypeModel as { name?: string } | undefined)?.name;
      nameBit = modelName ?? (i >= nBooked ? 'Piercing (acte ajouté)' : 'Piercing');
    }

    lines.push({
      key: act.id,
      label: `Acte ${act.sequence} — ${nameBit}`,
      amount: Math.max(0, amount),
    });
  }
  const subtotal = lines.reduce((s, l) => s + l.amount, 0);
  return { lines, subtotal };
}

const PiercingFinalizationPage: React.FC = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const {
    appointment,
    acts,
    loading,
    workflowSupplements: supplements,
    setWorkflowSupplements: setSupplements,
  } = usePiercingWorkflow();
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<AppointmentProduct | null>(null);
  const [productResults, setProductResults] = useState<AppointmentProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [addProductError, setAddProductError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { lines: piercingLines, subtotal: piercingSubtotal } = useMemo(
    () => buildPiercingSaleLines(acts, appointment?.pricing as Record<string, unknown> | undefined),
    [acts, appointment?.pricing],
  );

  const depositAmount =
    Number(
      appointment?.pricing?.depositAmount ??
        (appointment?.pricing as { deposit?: number } | undefined)?.deposit ??
        0,
    ) || 0;

  // Jewelry added during capture (derived from acts)
  const jewelryItems = acts
    .map((a) => {
      const jewelry = (a.demandDetails?.jewelry as any) ?? a.piercingRecord?.jewelry;
      if (!jewelry) return null;
      return {
        id: a.id,
        name: jewelry.material ? `Bijou — ${jewelry.material}` : `Bijou Acte ${a.sequence}`,
        price: jewelry.price ?? 0,
        qty: 1,
      };
    })
    .filter(Boolean) as WorkflowSupplement[];

  const supplementsTotal = supplements.reduce((sum, s) => sum + s.price * s.qty, 0);
  const jewelryTotal = jewelryItems.reduce((sum, j) => sum + j.price * j.qty, 0);
  const total = Math.max(0, piercingSubtotal + jewelryTotal + supplementsTotal - depositAmount);

  useEffect(() => {
    const query = newName.trim();
    if (!appointmentId || selectedProduct || query.length === 0) {
      setProductResults([]);
      setProductsLoading(false);
      return;
    }

    const controller = new AbortController();
    setProductsLoading(true);
    const timeout = window.setTimeout(() => {
      void apiClient(`/api/practitioner/appointments/${appointmentId}/products?q=${encodeURIComponent(query)}`, {
        signal: controller.signal,
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('Recherche de produits indisponible');
          const body = await response.json();
          setProductResults(Array.isArray(body.data?.products) ? body.data.products : []);
          setProductDropdownOpen(true);
        })
        .catch((fetchError) => {
          if (fetchError instanceof DOMException && fetchError.name === 'AbortError') return;
          setProductResults([]);
          setAddProductError('Impossible de rechercher les produits pour le moment.');
        })
        .finally(() => {
          if (!controller.signal.aborted) setProductsLoading(false);
        });
    }, 200);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [appointmentId, newName, selectedProduct]);

  const chooseProduct = (product: AppointmentProduct) => {
    setSelectedProduct(product);
    setNewName(`${product.productCode} — ${productDisplayName(product)}`);
    setNewPrice(product.price.toFixed(2).replace('.', ','));
    setProductDropdownOpen(false);
    setAddProductError(null);
  };

  const addSupplement = () => {
    setAddProductError(null);
    if (!selectedProduct) {
      setAddProductError('Sélectionnez un produit proposé dans la liste.');
      return;
    }
    setSupplements((prev) => [
    ...prev,
    {
      id: `product-${selectedProduct.id}-${Date.now()}`,
      productId: selectedProduct.id,
      productCode: selectedProduct.productCode,
      piercingTypeId: selectedProduct.piercingTypeId ?? undefined,
      name: productDisplayName(selectedProduct),
      price: selectedProduct.price,
      qty: 1,
    },
  ]);
    setNewName('');
    setNewPrice('');
    setSelectedProduct(null);
    setProductResults([]);
  };

  const removeSupp = (id: string) => setSupplements((prev) => prev.filter((s) => s.id !== id));
  const updateQty = (id: string, qty: number) => {
    if (qty < 1) { removeSupp(id); return; }
    setSupplements((prev) => prev.map((s) => s.id === id ? { ...s, qty } : s));
  };

  const handleComplete = async () => {
    if (!appointmentId || !appointment) return;
    setCompleting(true);
    setError(null);
    try {
      // Pricing + status in one call — PUT /appointments/:id is blocked for practitioners.
      const statusRes = await apiClient(`/api/practitioner/appointments/${appointmentId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'AWAITING_CHECKOUT',
          pricing: {
            ...(appointment.pricing ?? {}),
            workflowSupplements: supplements.map((s) => ({
              id: s.id,
              productId: s.productId,
              productCode: s.productCode,
              piercingTypeId: s.piercingTypeId,
              name: s.name,
              price: s.price,
              qty: s.qty,
            })),
          },
        }),
      });
      if (!statusRes.ok) {
        const errBody = await statusRes.json().catch(() => ({} as { message?: string }));
        throw new Error(errBody.message || 'Erreur lors de la finalisation');
      }
      navigate('/practitioner/dashboard', { state: { completedAppointmentId: appointmentId } });
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la finalisation.');
    } finally {
      setCompleting(false);
    }
  };

  if (loading || !appointment) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <>
      <PiercingWorkflowStepper currentStep={3} />

      <div className="grid grid-cols-12 gap-8">
        {/* Left: supplements input + list */}
        <div className="col-span-12 lg:col-span-7">
          <div className="card space-y-8">
            <header className="space-y-2">
              <h2 className="text-xl font-bold">Suppléments & finalisation du rendez-vous</h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Vérifiez les montants puis complétez la liste avec des produits ou suppléments facturés en caisse
                (crème, bijou additionnel, etc.). Le récapitulatif à droite se met à jour automatiquement.
              </p>
            </header>

            <section
              className="rounded-xl border-2 border-primary-200/90 bg-gradient-to-br from-primary-50/90 via-white to-amber-50/30 p-5 sm:p-6 shadow-sm ring-1 ring-primary-100/80"
              aria-labelledby="finalization-add-product-title"
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                <div
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-200/70 text-primary-800 shadow-inner"
                  aria-hidden
                >
                  <PackagePlus className="h-6 w-6" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <h3
                    id="finalization-add-product-title"
                    className="font-sans text-base font-bold tracking-tight text-gray-900"
                  >
                    Ajouter un produit à la commande
                  </h3>
                  <p className="text-sm text-gray-600 leading-snug">
                    Recherchez simplement un produit par son <strong>code article</strong>, son nom ou sa désignation.
                    La recherche porte sur l’ensemble du catalogue disponible.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-4">
                <div className="relative w-full">
                  <label htmlFor="supplement-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Désignation du produit
                  </label>
                  <input
                    id="supplement-name"
                    type="text"
                    className="input-field w-full text-gray-900 placeholder:text-gray-400"
                    placeholder="Ex. P000 ou nom du produit…"
                    value={newName}
                    onChange={(e) => {
                      setNewName(e.target.value);
                      setSelectedProduct(null);
                      setProductDropdownOpen(true);
                      setAddProductError(null);
                    }}
                    onFocus={() => newName.trim() && setProductDropdownOpen(true)}
                    onBlur={() => window.setTimeout(() => setProductDropdownOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      if (selectedProduct) addSupplement();
                      else if (productResults.length === 1) chooseProduct(productResults[0]);
                    }}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={productDropdownOpen}
                    aria-controls="supplement-product-options"
                    autoComplete="off"
                  />
                  {productDropdownOpen && newName.trim() && !selectedProduct && (
                    <div
                      id="supplement-product-options"
                      role="listbox"
                      className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl"
                    >
                      {productsLoading ? (
                        <p className="px-4 py-3 text-sm text-gray-500">Recherche…</p>
                      ) : productResults.length > 0 ? (
                        productResults.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            role="option"
                            aria-selected="false"
                            className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left hover:bg-primary-50 focus:bg-primary-50 focus:outline-none"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => chooseProduct(product)}
                          >
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-gray-900">
                                {product.productCode} — {productDisplayName(product)}
                              </span>
                              <span className="block truncate text-xs text-gray-500">
                                {product.description?.trim() || 'Produit du catalogue'}
                              </span>
                            </span>
                            <span className="shrink-0 text-sm font-semibold text-primary-700">
                              {product.price.toFixed(2)} €
                            </span>
                          </button>
                        ))
                      ) : (
                        <p className="px-4 py-3 text-sm text-gray-500">
                          Aucun produit ne correspond à cette recherche.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                  <div className="w-full sm:max-w-[200px]">
                    <label htmlFor="supplement-price" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-600">
                      Prix unitaire
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
                        €
                      </span>
                      <input
                        id="supplement-price"
                        type="text"
                        inputMode="decimal"
                        className="input-field w-full pl-8 text-gray-900 placeholder:text-gray-400"
                        placeholder="Sélectionnez un produit"
                        value={newPrice}
                        readOnly
                        aria-readonly="true"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-primary inline-flex h-12 w-full shrink-0 items-center justify-center gap-2 px-6 font-semibold sm:w-auto sm:min-w-[200px] disabled:cursor-not-allowed disabled:opacity-45"
                    onClick={addSupplement}
                    disabled={!selectedProduct}
                  >
                    <Plus className="h-5 w-5" strokeWidth={2.5} />
                    Ajouter à la liste
                  </button>
                </div>

                <p className="text-xs leading-relaxed text-gray-500">
                  Le prix est repris automatiquement depuis la fiche produit.
                </p>
              </div>

              {addProductError && (
                <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
                  {addProductError}
                </p>
              )}
            </section>

            <section className="space-y-4">
              <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-3">
                <Sparkles className="h-4 w-4 text-primary-600" aria-hidden />
                <h3 className="font-sans text-sm font-bold uppercase tracking-wider text-gray-700">
                  Détail des articles
                </h3>
              </div>

              {jewelryItems.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Bijoux & choix pendant la séance</p>
                  <div className="space-y-2">
                    {jewelryItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                          <Sparkles className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900">{item.name}</p>
                          {item.price > 0 && (
                            <p className="text-xs font-medium text-primary-700">{item.price.toFixed(2)} €</p>
                          )}
                        </div>
                        <span className="shrink-0 rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                          Inclus
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Produits & suppléments saisis</p>
                  {supplements.length === 0 && (
                    <span className="text-xs text-gray-400">Aucun pour l’instant — utilisez le bloc ci-dessus.</span>
                  )}
                </div>

                {jewelryItems.length === 0 && supplements.length === 0 && (
                  <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-8 text-center text-sm text-gray-500">
                    Aucune ligne pour ce rendez-vous : vous pouvez tout de suite ajouter un supplément avec le formulaire.
                  </p>
                )}

                <div className="space-y-3">
                  {supplements.map((s) => (
                    <div key={s.id} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                        <PackagePlus className="h-4 w-4" strokeWidth={2} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{s.name}</p>
                        <p className="text-xs font-medium text-primary-700">
                          {s.productCode ? `${s.productCode} · ` : ''}{s.price.toFixed(2)} €
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-300"
                          onClick={() => updateQty(s.id, s.qty - 1)}
                        >
                          —
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{s.qty}</span>
                        <button
                          type="button"
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-sm font-bold text-gray-700 hover:bg-gray-300"
                          onClick={() => updateQty(s.id, s.qty + 1)}
                        >
                          +
                        </button>
                        <button
                          type="button"
                          className="ml-1 text-gray-400 transition-colors hover:text-red-500"
                          onClick={() => removeSupp(s.id)}
                          aria-label={`Retirer ${s.name}`}
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* Right: price summary */}
        <div className="col-span-12 lg:col-span-5">
          <div className="card sticky top-4">
            <h3 className="font-bold text-lg mb-4">Résumé de la vente</h3>

            <div className="space-y-3 text-sm">
              {piercingLines.length > 0 ? (
                piercingLines.map((row) => (
                  <div key={row.key} className="flex justify-between gap-2">
                    <span className="text-gray-500 truncate" title={row.label}>{row.label}</span>
                    <span className="font-medium flex-shrink-0">{formatMoneyEur(row.amount)}€</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between">
                  <span className="text-gray-500">Prestations piercing</span>
                  <span className="font-medium">0€</span>
                </div>
              )}

              {jewelryItems.filter((j) => j.price > 0).map((j) => (
                <div key={j.id} className="flex justify-between">
                  <span className="text-gray-500">{j.name}</span>
                  <span className="font-medium">{formatMoneyEur(j.price)}€</span>
                </div>
              ))}

              {supplements.map((s) => (
                <div key={s.id} className="flex justify-between">
                  <span className="text-gray-500">{s.name} × {s.qty}</span>
                  <span className="font-medium">{formatMoneyEur(s.price * s.qty)}€</span>
                </div>
              ))}

              {depositAmount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Acompte versé</span>
                  <span className="font-medium">-{formatMoneyEur(depositAmount)}€</span>
                </div>
              )}

              <div className="border-t border-gray-200 pt-3 flex justify-between text-base font-bold">
                <span>Total</span>
                <span className="text-primary-600">{formatMoneyEur(total)}€</span>
              </div>
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              className="w-full btn-primary py-4 mt-6 flex items-center justify-center gap-2 disabled:opacity-50"
              onClick={handleComplete}
              disabled={completing}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
              </svg>
              <span className="font-bold text-sm uppercase tracking-wider">
                {completing ? 'Finalisation...' : 'Valider et passer en caisse'}
              </span>
            </button>

            <p className="text-xs text-gray-500 text-center mt-3 leading-relaxed">
              Le rendez-vous apparaît chez l’accueil en <strong>attente de caisse</strong>. L’encaissement clôt le dossier.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom back button */}
      <div className="fixed bottom-0 left-0 w-full bg-white/80 backdrop-blur border-t border-gray-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex gap-3">
          <button
            className="btn-secondary h-14 px-8 flex items-center gap-2 flex-shrink-0"
            onClick={() => navigate(`/practitioner/piercing/${appointmentId}/capture`)}
            disabled={completing}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Précédent
          </button>
        </div>
      </div>

      <div className="pb-28" />
    </>
  );
};

export default PiercingFinalizationPage;